import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { atualizarJson, enviarJson, pedirJson } from "./api";

/* Base de conhecimento: a Central de ajuda do cliente e o console da equipe.

   Duas famílias de endereço, e a diferença é de quem pode usar:
     · `/conhecimento/ajuda/...`  — leitura e avaliação (`conhecimento:ver`); o servidor só entrega
       o que a pessoa pode ler. A tela não repete a regra: mostra o que vier.
     · `/conhecimento/...`        — o console da equipe (`conhecimento:editar`). */

const CHAVE = ["conhecimento"] as const;

export type Visibilidade = "publica" | "organizacao" | "interna";
export type EstadoDoArtigo = "rascunho" | "em_revisao" | "publicado" | "arquivado";
export type AcaoNoArtigo = "enviar_revisao" | "devolver" | "publicar" | "arquivar" | "reabrir";

export const ROTULO_DA_VISIBILIDADE: Record<Visibilidade, string> = {
  publica: "Todos os clientes",
  organizacao: "Uma empresa",
  interna: "Só a equipe",
};

export const ROTULO_DO_ESTADO_DO_ARTIGO: Record<EstadoDoArtigo, string> = {
  rascunho: "Rascunho",
  em_revisao: "Em revisão",
  publicado: "Publicado",
  arquivado: "Arquivado",
};

export const ROTULO_DA_ACAO: Record<AcaoNoArtigo, string> = {
  enviar_revisao: "Enviar para revisão",
  devolver: "Devolver ao rascunho",
  publicar: "Publicar",
  arquivar: "Arquivar",
  reabrir: "Reabrir como rascunho",
};

export function tomDoEstadoDoArtigo(
  estado: EstadoDoArtigo,
): "ok" | "atencao" | "erro" | "neutro" | "aguardando" {
  if (estado === "publicado") return "ok";
  if (estado === "em_revisao") return "atencao";
  if (estado === "arquivado") return "neutro";
  return "aguardando";
}

/** Quem pode dar cada passo do ciclo — a tela só oferece o que o servidor vai aceitar. */
export const ACOES_DO_ESTADO: Record<
  EstadoDoArtigo,
  ReadonlyArray<{ acao: AcaoNoArtigo; publica: boolean }>
> = {
  rascunho: [
    { acao: "enviar_revisao", publica: false },
    { acao: "arquivar", publica: true },
  ],
  em_revisao: [
    { acao: "publicar", publica: true },
    { acao: "devolver", publica: true },
    { acao: "arquivar", publica: true },
  ],
  publicado: [{ acao: "arquivar", publica: true }],
  arquivado: [{ acao: "reabrir", publica: true }],
};

const esquemaCategoria = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  artigos: z.number().int().nonnegative().nullable().optional(),
});
export type CategoriaDeArtigo = z.infer<typeof esquemaCategoria>;

const esquemaArtigoNaBusca = z.object({
  id: z.string().uuid(),
  titulo: z.string(),
  resumo: z.string(),
  categoria_id: z.string().uuid().nullable(),
  categoria: z.string().nullable(),
  atualizado_em: z.string(),
  /* Módulo a que o artigo se aplica; nulo = vale para todos. */
  modulo_chave: z.string().nullable().optional(),
  modulo: z.string().nullable().optional(),
  /* De onde veio, e o link lá — o servidor só manda o link que o cliente consegue abrir. */
  origem: z.string().nullable().optional(),
  origem_url: z.string().nullable().optional(),
});
export type ArtigoNaBusca = z.infer<typeof esquemaArtigoNaBusca>;

const esquemaMinhaAvaliacao = z.object({ util: z.boolean(), comentario: z.string().nullable() });
export type MinhaAvaliacao = z.infer<typeof esquemaMinhaAvaliacao>;

const esquemaArtigoDaAjuda = esquemaArtigoNaBusca.extend({
  corpo: z.string(),
  minha_avaliacao: esquemaMinhaAvaliacao.nullable(),
});
export type ArtigoDaAjuda = z.infer<typeof esquemaArtigoDaAjuda>;

const esquemaVisibilidade = z.enum(["publica", "organizacao", "interna"]);
const esquemaEstado = z.enum(["rascunho", "em_revisao", "publicado", "arquivado"]);

const esquemaArtigoNaLista = z.object({
  id: z.string().uuid(),
  titulo: z.string(),
  resumo: z.string(),
  categoria_id: z.string().uuid().nullable(),
  categoria: z.string().nullable(),
  visibilidade: esquemaVisibilidade,
  organizacao_id: z.string().uuid().nullable(),
  organizacao: z.string().nullable(),
  estado: esquemaEstado,
  versao_atual: z.number().int().positive(),
  autor: z.string().nullable(),
  revisor: z.string().nullable(),
  origem_ticket_id: z.string().uuid().nullable(),
  modulo_chave: z.string().nullable().optional(),
  modulo: z.string().nullable().optional(),
  origem: z.string().nullable().optional(),
  origem_url: z.string().nullable().optional(),
  origem_atualizada_em: z.string().nullable().optional(),
  atualizado_em: z.string(),
  publicado_em: z.string().nullable(),
  avaliacoes_uteis: z.number().int().nonnegative(),
  avaliacoes_nao_uteis: z.number().int().nonnegative(),
});
export type ArtigoNaLista = z.infer<typeof esquemaArtigoNaLista>;

const esquemaVersaoNaLista = z.object({
  numero: z.number().int().positive(),
  titulo: z.string(),
  autor: z.string().nullable(),
  criada_em: z.string(),
  publicada_em: z.string().nullable(),
});

const esquemaArtigoNoConsole = esquemaArtigoNaLista.extend({
  corpo: z.string(),
  versoes: z.array(esquemaVersaoNaLista),
});
export type ArtigoNoConsole = z.infer<typeof esquemaArtigoNoConsole>;

const esquemaResultadoDaEdicao = z.object({
  artigo: esquemaArtigoNoConsole,
  nova_versao: z.boolean(),
});

const esquemaVersao = z.object({
  numero: z.number().int().positive(),
  titulo: z.string(),
  resumo: z.string(),
  corpo: z.string(),
  autor: z.string().nullable(),
  criada_em: z.string(),
  publicada_em: z.string().nullable(),
});

const esquemaAvaliacaoNoConsole = z.object({
  util: z.boolean(),
  comentario: z.string().nullable(),
  versao: z.number().int().positive(),
  organizacao: z.string(),
  pessoa: z.string(),
  atualizada_em: z.string(),
});

const esquemaMetricas = z.object({
  artigos_publicados: z.number().int().nonnegative(),
  desistencias: z.number().int().nonnegative(),
  aberturas_apos_leitura: z.number().int().nonnegative(),
  taxa_de_deflexao: z.number().nullable(),
  avaliacoes_uteis: z.number().int().nonnegative(),
  avaliacoes_nao_uteis: z.number().int().nonnegative(),
  artigos: z.array(
    z.object({
      artigo_id: z.string().uuid(),
      titulo: z.string(),
      desistencias: z.number().int().nonnegative(),
      aberturas: z.number().int().nonnegative(),
      util: z.number().int().nonnegative(),
      nao_util: z.number().int().nonnegative(),
    }),
  ),
});

const esquemaOrganizacao = z.object({
  id: z.string().uuid(),
  razao_social: z.string(),
  nome_fantasia: z.string().nullable(),
});

const esquemaConsultaRegistrada = z.object({ id: z.string().uuid() });

export interface CorpoDoArtigo {
  titulo: string;
  resumo: string;
  corpo: string;
  categoria_id: string | null;
  /* Nulo = artigo geral, vale para todo cliente. Sempre enviado: omitir limparia o módulo. */
  modulo_chave: string | null;
  visibilidade: Visibilidade;
  organizacao_id: string | null;
}

const esquemaModulo = z.object({ chave: z.string(), nome: z.string() });
export type ModuloDoCatalogo = z.infer<typeof esquemaModulo>;

export const ROTULO_DA_ORIGEM: Record<string, string> = {
  central_de_ajuda: "Central de ajuda",
  geodocs: "GEOdocs",
};

/** Os valores especiais que o console aceita nos filtros de origem e de módulo. */
export const ORIGEM_ESCRITO_AQUI = "escrito_aqui";
export const SEM_MODULO = "sem_modulo";

const esquemaItemDaBusca = z.object({
  origem: z.enum(["ajuda", "materiais", "chamados"]),
  id: z.string().uuid(),
  titulo: z.string(),
  trecho: z.string(),
  modulo_chave: z.string().nullable(),
  modulo: z.string().nullable(),
  categoria: z.string().nullable(),
  quando: z.string().nullable(),
  rota: z.string(),
});
export type ItemDaBusca = z.infer<typeof esquemaItemDaBusca>;
export type OrigemDaBusca = ItemDaBusca["origem"];

export const ROTULO_DA_ORIGEM_DA_BUSCA: Record<OrigemDaBusca, string> = {
  ajuda: "Central de ajuda",
  materiais: "Materiais",
  chamados: "Meus chamados",
};

const esquemaBuscaUnificada = z.object({
  termo: z.string(),
  so_meus_modulos: z.boolean(),
  modulos_da_empresa: z.array(esquemaModulo),
  itens: z.array(esquemaItemDaBusca),
  totais: z.object({ ajuda: z.number(), materiais: z.number(), chamados: z.number() }),
});
export type BuscaUnificada = z.infer<typeof esquemaBuscaUnificada>;

function consulta(parametros: Record<string, string | undefined | null | boolean>): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor === true) busca.set(chave, "true");
    else if (typeof valor === "string" && valor) busca.set(chave, valor);
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : "";
}

/**
 * O valor só muda depois de `espera` ms sem mudar: a digitação não vira uma consulta por tecla.
 * Usado na busca da Central e nas sugestões de artigo ao abrir chamado.
 */
export function useValorAdiado<T>(valor: T, espera = 350): T {
  const [adiado, setAdiado] = useState(valor);
  useEffect(() => {
    const temporizador = setTimeout(() => setAdiado(valor), espera);
    return () => clearTimeout(temporizador);
  }, [valor, espera]);
  return adiado;
}

/* -------------------------------------------------------------- Central de ajuda (cliente) */

export function useCategoriasDaAjuda(soMeusModulos = false) {
  return useQuery({
    queryKey: [...CHAVE, "ajuda", "categorias", soMeusModulos],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/ajuda/categorias${consulta({ so_meus_modulos: soMeusModulos })}`,
        z.array(esquemaCategoria),
        { signal },
      ),
  });
}

export function useBuscaNaAjuda(texto: string, categoriaId: string, soMeusModulos = false) {
  return useQuery({
    queryKey: [...CHAVE, "ajuda", "artigos", texto, categoriaId, soMeusModulos],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/ajuda/artigos${consulta({
          q: texto,
          categoria_id: categoriaId,
          so_meus_modulos: soMeusModulos,
        })}`,
        z.array(esquemaArtigoNaBusca),
        { signal },
      ),
  });
}

/** Menos de 2 letras não é assunto: a busca unificada nem sai. */
export const MINIMO_DE_LETRAS_DA_BUSCA = 2;

/**
 * Central de ajuda + materiais + chamados da própria empresa numa consulta só. `soMeusModulos`
 * vai SEMPRE explícito (o padrão do servidor é ligado): desligar precisa chegar como `false`.
 */
export function useBuscaUnificada(texto: string, soMeusModulos: boolean) {
  const termo = texto.trim();
  return useQuery({
    queryKey: [...CHAVE, "ajuda", "busca", termo, soMeusModulos],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/ajuda/busca${consulta({ q: termo, so_meus_modulos: soMeusModulos ? "true" : "false" })}`,
        esquemaBuscaUnificada,
        { signal },
      ),
    enabled: termo.length >= MINIMO_DE_LETRAS_DA_BUSCA,
  });
}

/** Menos de 4 letras não é assunto — não consulta até haver o que procurar. */
export const MINIMO_DE_LETRAS_DA_SUGESTAO = 4;

export function useSugestoesDeArtigos(assunto: string, habilitada = true) {
  const texto = assunto.trim();
  return useQuery({
    queryKey: [...CHAVE, "ajuda", "sugestoes", texto],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/ajuda/artigos${consulta({ q: texto, sugestao: true })}`,
        z.array(esquemaArtigoNaBusca),
        { signal },
      ),
    enabled: habilitada && texto.length >= MINIMO_DE_LETRAS_DA_SUGESTAO,
  });
}

export function chaveDoArtigoDaAjuda(id: string) {
  return [...CHAVE, "ajuda", "artigo", id] as const;
}

export function useArtigoDaAjuda(id: string, habilitada = true) {
  return useQuery({
    queryKey: chaveDoArtigoDaAjuda(id),
    queryFn: ({ signal }) =>
      pedirJson(`/conhecimento/ajuda/artigos/${encodeURIComponent(id)}`, esquemaArtigoDaAjuda, {
        signal,
      }),
    enabled: habilitada && Boolean(id),
  });
}

export function useAvaliarArtigo(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { util: boolean; comentario: string | null }) =>
      atualizarJson(
        `/conhecimento/ajuda/artigos/${encodeURIComponent(id)}/avaliacao`,
        esquemaMinhaAvaliacao,
        corpo,
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDoArtigoDaAjuda(id) }),
  });
}

export type DesfechoDaConsulta = "desistiu" | "abriu_chamado";

export function useRegistrarConsulta() {
  return useMutation({
    mutationFn: (corpo: { artigo_id: string; assunto: string; desfecho: DesfechoDaConsulta }) =>
      enviarJson("/conhecimento/ajuda/consultas", esquemaConsultaRegistrada, corpo),
  });
}

/* ---------------------------------------------------------------------- console da equipe */

export function useCategoriasDeArtigo(habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, "categorias"],
    queryFn: ({ signal }) =>
      pedirJson("/conhecimento/categorias", z.array(esquemaCategoria), { signal }),
    enabled: habilitada,
  });
}

export function useCriarCategoriaDeArtigo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nome: string) => enviarJson("/conhecimento/categorias", esquemaCategoria, { nome }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "categorias"] }),
  });
}

export interface FiltrosDosArtigos {
  estado?: EstadoDoArtigo | "";
  categoria_id?: string;
  visibilidade?: Visibilidade | "";
  q?: string;
  /* `escrito_aqui`, `central_de_ajuda`, `geodocs`... */
  origem?: string;
  /* A chave do módulo, ou `sem_modulo`. */
  modulo?: string;
}

export function useModulosDoCatalogo(habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, "modulos"],
    queryFn: ({ signal }) => pedirJson("/conhecimento/modulos", z.array(esquemaModulo), { signal }),
    enabled: habilitada,
    staleTime: 5 * 60_000,
  });
}

export function useArtigosDoConsole(filtros: FiltrosDosArtigos, habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, "artigos", filtros],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/artigos${consulta({ ...filtros })}`,
        z.array(esquemaArtigoNaLista),
        { signal },
      ),
    enabled: habilitada,
  });
}

export function chaveDoArtigo(id: string) {
  return [...CHAVE, "artigo", id] as const;
}

export function useArtigoNoConsole(id: string) {
  return useQuery({
    queryKey: chaveDoArtigo(id),
    queryFn: ({ signal }) =>
      pedirJson(`/conhecimento/artigos/${encodeURIComponent(id)}`, esquemaArtigoNoConsole, {
        signal,
      }),
    enabled: Boolean(id),
  });
}

function useInvalidarArtigos() {
  const cliente = useQueryClient();
  return (id?: string) => {
    void cliente.invalidateQueries({ queryKey: [...CHAVE, "artigos"] });
    void cliente.invalidateQueries({ queryKey: [...CHAVE, "metricas"] });
    void cliente.invalidateQueries({ queryKey: [...CHAVE, "ajuda"] });
    if (id) void cliente.invalidateQueries({ queryKey: chaveDoArtigo(id) });
  };
}

export function useCriarArtigo() {
  const invalidar = useInvalidarArtigos();
  return useMutation({
    mutationFn: (corpo: CorpoDoArtigo) =>
      enviarJson("/conhecimento/artigos", esquemaArtigoNoConsole, corpo),
    onSuccess: (artigo) => invalidar(artigo.id),
  });
}

export function useEditarArtigo(id: string) {
  const invalidar = useInvalidarArtigos();
  return useMutation({
    mutationFn: (corpo: CorpoDoArtigo) =>
      atualizarJson(
        `/conhecimento/artigos/${encodeURIComponent(id)}`,
        esquemaResultadoDaEdicao,
        corpo,
      ),
    onSuccess: () => invalidar(id),
  });
}

export function useAcaoNoArtigo(id: string) {
  const invalidar = useInvalidarArtigos();
  return useMutation({
    mutationFn: (acao: AcaoNoArtigo) =>
      enviarJson(`/conhecimento/artigos/${encodeURIComponent(id)}/acoes`, esquemaArtigoNoConsole, {
        acao,
      }),
    onSuccess: () => invalidar(id),
  });
}

export function useVersaoDoArtigo(id: string, numero: number | null) {
  return useQuery({
    queryKey: [...CHAVE, "artigo", id, "versao", numero],
    queryFn: ({ signal }) =>
      pedirJson(`/conhecimento/artigos/${encodeURIComponent(id)}/versoes/${numero}`, esquemaVersao, {
        signal,
      }),
    enabled: numero !== null,
  });
}

export function useAvaliacoesDoArtigo(id: string) {
  return useQuery({
    queryKey: [...CHAVE, "artigo", id, "avaliacoes"],
    queryFn: ({ signal }) =>
      pedirJson(
        `/conhecimento/artigos/${encodeURIComponent(id)}/avaliacoes`,
        z.array(esquemaAvaliacaoNoConsole),
        { signal },
      ),
    enabled: Boolean(id),
  });
}

export function useMetricasDaBase(habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, "metricas"],
    queryFn: ({ signal }) => pedirJson("/conhecimento/metricas", esquemaMetricas, { signal }),
    enabled: habilitada,
  });
}

export function useOrganizacoesParaArtigo(habilitada = true) {
  return useQuery({
    queryKey: [...CHAVE, "organizacoes"],
    queryFn: ({ signal }) =>
      pedirJson("/organizacoes", z.array(esquemaOrganizacao), { signal }),
    enabled: habilitada,
  });
}

export function useArtigoDeChamado() {
  const invalidar = useInvalidarArtigos();
  return useMutation({
    mutationFn: (corpo: { ticket_id: string; acao_id: string }) =>
      enviarJson("/conhecimento/artigos/de-chamado", esquemaArtigoNoConsole, corpo),
    onSuccess: (artigo) => invalidar(artigo.id),
  });
}
